import type { Exchange } from '@urql/core';
// eslint-disable-next-line import/no-extraneous-dependencies
import { makeOperation } from '@urql/core';
import { authExchange as urqlAuthExchange } from '@urql/exchange-auth';

import { AuthStorage } from '@order/AuthMachine';

/**
 * # Urql Auth Exchange
 *
 * Reads from storage and adds the csrf token headers to the outgoing request
 *
 * `getAuth` - reads the csrf token from storage, via out cross platform storage adapter
 * `addAuthToOperation` - adds the csrf header to the outgoing request
 * `willAuthError` - returns true every time to force re-execution of the getAuth function
 *
 * @see {@link: https://formidable.com/open-source/urql/docs/advanced/authentication/}
 * @see {@link: https://formidable.com/open-source/urql/docs/api/auth-exchange/}
 */

// ────────────────────────────────────────────────────────────────────────────────

// For outgoing graphql API requests
const CSRF_HEADER_KEY = 'x-csrf-token';

// ────────────────────────────────────────────────────────────────────────────────

type AuthState = null | { readonly csrfToken: string };

// ────────────────────────────────────────────────────────────────────────────────

export const authExchange: Exchange = urqlAuthExchange<AuthState>({
  async getAuth() {
    try {
      const csrfToken = await AuthStorage.getToken();

      return csrfToken ? { csrfToken } : null;
    } catch {
      return null;
    }
  },

  /**
   * Runs before a network request is made
   * Returning true forces the `getAuth` to re-read from storage every request
   * (assumes that the logout workflow clears/updates the token from storage)
   */
  willAuthError: () => true,

  addAuthToOperation({ authState, operation }) {
    if (!authState?.csrfToken) return operation;

    const fetchOptions =
      typeof operation.context.fetchOptions === 'function'
        ? operation.context.fetchOptions()
        : operation.context.fetchOptions ?? {};

    return makeOperation(operation.kind, operation, {
      ...operation.context,
      fetchOptions: {
        ...fetchOptions,
        headers: {
          ...fetchOptions.headers,
          [CSRF_HEADER_KEY]: authState.csrfToken,
        },
      },
    });
  },
});
